/* eslint-disable @typescript-eslint/no-unused-vars */
import { css } from '@emotion/react'
import { FC, useCallback } from 'react'
import { Field, useField } from 'react-final-form'

import ConfirmText from '~/form/components/atoms/ConfirmText'
import HelperText from '~/form/components/atoms/HelperText'
import IconCancel from '~/form/components/atoms/icons/IconCancel'

import type { Props as UrlFileFieldProps } from './UrlFileField'

type Props = {
  dataName: string
} & Pick<UrlFileFieldProps, 'fileAccept' | 'isConfirm'>

const ACCEPT_FILE_EXT =
  /(\.jpg|\.jpeg|\.png|\.txt|\.word|\.pdf|\.xlsx?|\.docx?)$/i

const InputFile: FC<Props> = ({ dataName, fileAccept, isConfirm }) => {
  const file = `${dataName}.file`
  const data = `${dataName}.data`
  const fileNameName = `${dataName}.name`
  const fileSizeName = `${dataName}.size`

  const fileField = useField(file)
  const dataField = useField(data)
  const fileNameField = useField(fileNameName)
  const fileSizeField = useField(fileSizeName)

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files || event.target.files.length <= 0) {
        return
      }
      const file = event.target.files[0]

      const reader = new FileReader()

      reader.addEventListener('load', () => {
        if (typeof reader.result === 'string') {
          fileField.input.onChange(file)
          fileNameField.input.onChange(file.name)
          fileSizeField.input.onChange(file.size)
          dataField.input.onChange(reader.result)
          dataField.input.onBlur()
        }
      })

      reader.readAsDataURL(file)
    },
    [dataField.input, fileField.input, fileNameField.input, fileSizeField.input]
  )
  const handleFileDelete = useCallback(() => {
    fileNameField.input.onChange(undefined)
    fileSizeField.input.onChange(undefined)
    fileField.input.onChange(undefined)
    dataField.input.onChange(undefined)
    dataField.input.onBlur()
  }, [
    dataField.input,
    fileField.input,
    fileNameField.input,
    fileSizeField.input,
  ])

  if (isConfirm && !!!fileNameField.input.value) {
    return null
  }

  if (isConfirm && !!fileNameField.input.value) {
    return <ConfirmText value={fileNameField.input.value} />
  }

  const isMultipleConfirm = isConfirm && fileNameField.input.value === ''

  return (
    <Field
      name={data}
      css={isMultipleConfirm && hiddenStyle}
      validate={(value: File) => {
        if (!value) return

        /** ファイルサイズチェック */
        const maxMB = 5
        if (fileSizeField.input.value > maxMB * 1000000) {
          return `ファイルサイズは${maxMB}MB以下で送付してください`
        }

        /** 許可されたファイルタイプ */
        if (!ACCEPT_FILE_EXT.exec(fileNameField.input.value)) {
          return `許可されていないファイル形式です`
        }
      }}
    >
      {() => (
        <div>
          {dataField.input.value ? (
            <div css={fileTextStyle}>
              <p>{fileNameField.input.value}</p>
              <button
                type='button'
                css={cancelButtonStyle}
                onClick={handleFileDelete}
                aria-label='削除'
              >
                <IconCancel />
              </button>
            </div>
          ) : (
            <div css={fileInputFieldStyle()}>
              <label>
                <input
                  type='file'
                  accept={fileAccept}
                  onChange={handleFileChange}
                />
                ファイルを選択
              </label>
            </div>
          )}
          <HelperText text='5MB以内のファイルをアップロードしてください' />
        </div>
      )}
    </Field>
  )
}

export default InputFile

const fileTextStyle = css`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 20px;
  line-height: calc(24 / 14);
  color: #fff;
`
const cancelButtonStyle = css`
  position: relative;
  padding: 3px 3px 1px;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 44px;
    height: 44px;
    transform: translate(-50%, -50%);
  }

  svg {
    font-size: 20px;
    vertical-align: middle;
  }
`

const fileInputFieldStyle = () => css`
  font-size: 14px;

  input {
    display: none;
  }

  label {
    color: #000;
    background-color: #fbe21f;
    border-radius: 15px;
    cursor: pointer;
    display: inline-block;
    font-size: 12px;
    line-height: 30px;
    padding: 0 18px;
  }
`
const hiddenStyle = css`
  display: none;
`
